import { Component, computed, inject, OnInit, signal } from '@angular/core'
import { OrderBaseComponent } from '../order-base.component'
import { PRODUCT_TYPES, ProductStatusType, ServiceType } from '../../../common/models/order/order.model'
import { SQResponseStateService } from '../../../common/store/state/order/orderv2/order-api-response/sq-response.service'
import { Message } from '../../../common/enums/message.enum'
import { MenuItem } from 'primeng/api'

@Component({
  selector: 'app-order-navigation',
  templateUrl: './order-navigation.component.html',
  styleUrl: './order-navigation.component.scss',
  standalone: false
})
export class OrderNavigationComponent extends OrderBaseComponent implements OnInit {
  sqResponseStateService = inject(SQResponseStateService)
  currentFlowType = this.orderStateServiceV2.currentFlowType
  readonly productTypes = PRODUCT_TYPES
  visible = false
  orderOptionsOpen = false
  removeConfirmationVisible = false

  orderOptionItems: MenuItem[] = [
    {
      label: 'Remove product',
      icon: 'pi pi-minus-circle',
      styleClass: 'text-red-600 font-semibold',
      command: () => this.removeProductClicked()
    }
  ]

  ngOnInit() {
    switch (this.currentFlowType()) {
      case 'Internet':
        this.selectProduct(PRODUCT_TYPES.INTERNET)
        break
      case 'Video':
        this.selectProduct(PRODUCT_TYPES.VIDEO)
        break
      case 'Voice':
        this.selectProduct(PRODUCT_TYPES.VOICE)
        break
      case 'Wireless':
        this.selectProduct(PRODUCT_TYPES.WIRELESS)
        break
    }
  }

  readonly selectedProduct = signal<ServiceType | null>(null)

  products = computed(() => {
    const statuses = this.orderStateServiceV2.serviceStatuses()

    return [
      {
        type: PRODUCT_TYPES.INTERNET,
        label: 'Internet',
        icon: 'pi pi-globe',
        route: this.orderRoutes.INTERNET_PACKAGE_SELECTION,
        isAvailable: this.sqResponseStateService.hasInternetProduct()
      },
      {
        type: PRODUCT_TYPES.WIRELESS,
        label: 'Wireless',
        icon: 'pi pi-chart-bar',
        route: this.orderRoutes.WIRELESS_SETUP,
        isAvailable: this.sqResponseStateService.hasWirelessProduct()
      },
      {
        type: PRODUCT_TYPES.VIDEO,
        label: 'Video',
        icon: 'pi pi-video',
        route: this.orderRoutes.VIDEO_PACKAGE_SELECTION,
        isAvailable: this.sqResponseStateService.hasVideoProduct()
      },
      {
        type: PRODUCT_TYPES.VOICE,
        label: 'Voice',
        icon: 'pi pi-microphone',
        route: this.orderRoutes.VOICE_PACKAGE_SELECTION,
        isAvailable: this.sqResponseStateService.hasVoiceProduct()
      },
      {
        type: PRODUCT_TYPES.PROTECTION,
        label: 'Protection',
        icon: 'pi pi-shield',
        route: null,
        isAvailable: this.sqResponseStateService.hasProtectionProduct()
      }
    ]
      .filter(product => product.isAvailable)
      .map(product => {
        let status = statuses.find(s => s.key === product.type)?.status;
        if (product.type === PRODUCT_TYPES.VOICE && !this.isInternetCompleted()) {
          status = ProductStatusType.REQUIRES_ATTENTION;
        }
        return {
          ...product,
          statusIcon: this.#statusIcon(status),
          tooltip: `${product.label} - ${this.#statusDescription(status)}`
        }
      })
  })

  selectedProductLabel = computed(() => {
    return this.products().find(p => p.type === this.selectedProduct())?.label ?? ''
  })

  packageGroups = computed(() => {
    const product = this.selectedProduct()
    return product ? (product === PRODUCT_TYPES.VIDEO ? this.sqResponseStateService.videoPackageGroups() : this.sqResponseStateService.packageGroups(product)) : []
  })

  inProgressProduct = computed(() => {
    return this.products().find(p => {
      const status = this.orderStateServiceV2.serviceStatuses().find(s => s.key === p.type)?.status
      return status === ProductStatusType.STARTED_BUT_NOT_FINISHED
    })
  })

  currentOrderLabel = computed(() => this.inProgressProduct()?.label ?? this.selectedProductLabel())

  open(): void {
    const currentProductType = this.#currentProductType()
    const nextProductType = this.products().some(product => product.type === currentProductType)
      ? currentProductType
      : this.products()[0]?.type ?? null

    this.selectedProduct.set(nextProductType)
    this.orderOptionsOpen = false
    this.removeConfirmationVisible = false
    this.visible = true
  }

  selectProduct(productType: ServiceType): void {
    if (productType === this.selectedProduct()) {
      return
    }

    this.selectedProduct.set(productType)
    this.removeConfirmationVisible = false
  }

  goToPackages(): void {
    const productType = this.selectedProduct()
    if (productType === PRODUCT_TYPES.VOICE && !this.isInternetCompleted()) {
      this.notificationService.showError(Message.VoiceFlowInternetRequiredErrorMessage);
      return
    }
    const route = this.products().find(p => p.type === productType)?.route
    if (route) {
      this.visible = false
      this.navigate([route])
    }
  }

  continueOrder(): void {
    this.visible = false
  }

  removeProductClicked(): void {
    this.removeConfirmationVisible = true
    this.orderOptionsOpen = false
  }

  keepProduct(): void {
    this.removeConfirmationVisible = false
  }

  confirmRemoveProduct(): void {
    this.visible = false
    this.removeProduct()
  }

  #currentProductType(): ServiceType | null {
    switch (this.currentFlowType()) {
      case 'Internet':
        return PRODUCT_TYPES.INTERNET
      case 'Video':
        return PRODUCT_TYPES.VIDEO
      case 'Voice':
        return PRODUCT_TYPES.VOICE
      case 'Wireless':
        return PRODUCT_TYPES.WIRELESS
      default:
        return null
    }
  }

  #statusIcon(status?: ProductStatusType): string {
    switch (status) {
      case ProductStatusType.COMPLETED:
        return 'pi pi-bundle-green-check'
      case ProductStatusType.STARTED_BUT_NOT_FINISHED:
        return 'pi pi-bundle-exclamation-orange'
      case ProductStatusType.NOT_AVAILABLE:
        return 'pi pi-bundle-red-x'
      case ProductStatusType.REQUIRES_ATTENTION:
        return 'pi pi-bundle-exclamation-red'
      default:
        return 'pi pi-bundle-minus'
    }
  }

  #statusDescription(status?: ProductStatusType): string {
    switch (status) {
      case ProductStatusType.COMPLETED:
        return 'Completed'
      case ProductStatusType.STARTED_BUT_NOT_FINISHED:
        return 'Order in progress'
      case ProductStatusType.NOT_AVAILABLE:
        return 'Not available'
      case ProductStatusType.REQUIRES_ATTENTION:
        return 'Requires attention'
      default:
        return 'Available to start'
    }
  }
}